import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead, { SITE_URL } from '../../components/feature/SEOHead';

const disclaimerSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Disclaimer — CallOfDutyNext.com',
    description:
      'CallOfDutyNext.com is an independent fan news site. Not affiliated with or endorsed by Activision Blizzard or Microsoft. Call of Duty® is a registered trademark of Activision Publishing, Inc.',
    url: `${SITE_URL}/disclaimer`,
    isPartOf: { '@type': 'WebSite', name: 'CallOfDutyNext.com', url: SITE_URL },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Disclaimer', item: `${SITE_URL}/disclaimer` },
    ],
  },
];

function SectionBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-[#1e1e1e] mb-0">
      <div className="border-b border-[#1e1e1e] px-6 py-4 bg-[#0d0d0d]">
        <h2 className="font-['Barlow_Condensed'] font-black text-white text-lg uppercase tracking-widest">
          {title}
        </h2>
      </div>
      <div className="px-6 py-6 text-[#888] text-sm leading-relaxed space-y-3">
        {children}
      </div>
    </div>
  );
}

export default function DisclaimerPage() {
  return (
    <div className="bg-[#111] min-h-screen">
      <SEOHead
        title="Disclaimer — CallOfDutyNext.com"
        description="CallOfDutyNext.com is an independent fan news site. Not affiliated with or endorsed by Activision Blizzard or Microsoft. Call of Duty® is a registered trademark of Activision Publishing, Inc."
        keywords="disclaimer, fan site, not affiliated, Activision, Blizzard, Microsoft, Call of Duty trademark"
        canonical="/disclaimer"
        noIndex={false}
        schema={disclaimerSchema}
      />
      <Navbar />

      <div className="pt-14">
        {/* Title bar */}
        <div className="border-b border-[#1e1e1e] bg-[#0d0d0d]">
          <div className="max-w-4xl mx-auto px-6 py-5">
            <div className="flex items-center gap-3 mb-1">
              <span className="font-['Barlow_Condensed'] font-bold text-xs tracking-[0.2em] text-[#555] uppercase border border-[#2a2a2a] px-2 py-0.5">
                Legal
              </span>
            </div>
            <h1 className="font-['Barlow_Condensed'] font-black text-white text-3xl uppercase tracking-wider leading-none">
              Disclaimer
            </h1>
            <p className="text-[#555] text-sm mt-1">
              Important information about this fan-run, independent news site.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-6 py-10">

          {/* Highlight box */}
          <div className="border border-[#2a2a2a] bg-[#0d0d0d] mb-8 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center shrink-0 bg-[#1a1a1a] border border-[#2a2a2a]">
                <i className="ri-information-line text-[#00ff41] text-lg" />
              </div>
              <div>
                <p className="font-['Barlow_Condensed'] font-bold text-white text-base uppercase tracking-wide mb-2">
                  Independent Fan Site
                </p>
                <p className="text-[#888] text-sm leading-relaxed">
                  <strong className="text-[#aaa]">CallOfDutyNext.com</strong> is an independent fan-run news and information website.
                  It is <strong className="text-white">not affiliated with, endorsed by, sponsored by, or in any way officially connected</strong> to
                  Activision Blizzard, Inc., Microsoft Corporation, or any of their subsidiaries or affiliates.
                </p>
              </div>
            </div>
          </div>

          {/* Sections stacked borderless style */}
          <div className="border border-[#1e1e1e] divide-y divide-[#1e1e1e] mb-8">

            {/* Trademark */}
            <div className="px-6 py-7">
              <h2 className="font-['Barlow_Condensed'] font-black text-white text-xl uppercase tracking-widest mb-4">
                Trademark Notice
              </h2>
              <p className="text-[#888] text-sm leading-relaxed mb-3">
                <strong className="text-[#ccc]">Call of Duty®</strong> is a registered trademark of <strong className="text-[#ccc]">Activision Publishing, Inc.</strong>
                All related game names, logos, and marks are trademarks and/or registered trademarks of Activision Blizzard, Inc.
                or its affiliates in the U.S. and other countries.
              </p>
              <p className="text-[#888] text-sm leading-relaxed">
                The use of these trademarks on this site is for reference and informational purposes only and does not imply
                any association with or endorsement by the trademark holders.
              </p>
            </div>

            {/* Not affiliated */}
            <div className="px-6 py-7">
              <h2 className="font-['Barlow_Condensed'] font-black text-white text-xl uppercase tracking-widest mb-4">
                Non-Affiliation with Activision, Blizzard &amp; Microsoft
              </h2>
              <p className="text-[#888] text-sm leading-relaxed mb-3">
                This website is operated independently by fans of the Call of Duty franchise.
                <strong className="text-[#ccc]"> Activision Blizzard, Inc.</strong> (including its studios such as Infinity Ward, Treyarch,
                Sledgehammer Games, and Raven Software) and <strong className="text-[#ccc]">Microsoft Corporation</strong> have not authorized,
                sponsored, or endorsed this site or its content in any way.
              </p>
              <p className="text-[#888] text-sm leading-relaxed">
                All editorial opinions, reviews, analysis, and commentary published on CallOfDutyNext.com represent the views of the
                site&apos;s independent contributors only — not those of Activision Blizzard or Microsoft.
              </p>
            </div>

            {/* Content sources */}
            <div className="px-6 py-7">
              <h2 className="font-['Barlow_Condensed'] font-black text-white text-xl uppercase tracking-widest mb-4">
                Content &amp; Sources
              </h2>
              <p className="text-[#888] text-sm leading-relaxed mb-3">
                News, patch notes, and analysis published on this site are compiled from publicly available sources,
                official developer communications, community reports, and original editorial work.
                We strive for accuracy but make no warranties regarding the completeness or timeliness of any information.
              </p>
              <p className="text-[#888] text-sm leading-relaxed">
                Game footage, screenshots, and in-game assets referenced are the property of their respective rights holders
                and are used here for informational, commentary, and fan-culture purposes under fair use principles.
              </p>
            </div>

            {/* Liability */}
            <div className="px-6 py-7">
              <h2 className="font-['Barlow_Condensed'] font-black text-white text-xl uppercase tracking-widest mb-4">
                Limitation of Liability
              </h2>
              <p className="text-[#888] text-sm leading-relaxed">
                CallOfDutyNext.com is provided &quot;as is&quot; without any warranties, express or implied. We are not liable for any
                errors, omissions, or inaccuracies in the information provided, nor for any loss or damage arising from reliance on
                the content published on this site. All information is shared for entertainment and informational purposes only.
              </p>
            </div>

            {/* Contact */}
            <div className="px-6 py-7">
              <h2 className="font-['Barlow_Condensed'] font-black text-white text-xl uppercase tracking-widest mb-4">
                Copyright Concerns
              </h2>
              <p className="text-[#888] text-sm leading-relaxed">
                If you believe any content on this site infringes upon intellectual property rights, please{' '}
                <Link to="/contact" className="text-[#00ff41] hover:underline cursor-pointer">
                  contact us
                </Link>{' '}
                and we will review and address the matter promptly.
              </p>
            </div>
          </div>

          {/* Last updated */}
          <div className="flex items-center justify-between border border-[#1e1e1e] bg-[#0d0d0d] px-6 py-4 mb-8">
            <p className="text-[#555] text-xs tracking-wide uppercase font-['Barlow_Condensed'] font-bold">
              Last Updated
            </p>
            <p className="text-[#666] text-xs">March 2026</p>
          </div>

          {/* Nav links */}
          <div className="flex items-center gap-6">
            <Link
              to="/about"
              className="text-[#555] hover:text-white text-sm transition-colors cursor-pointer flex items-center gap-2"
            >
              <i className="ri-arrow-left-line text-xs" />
              About Us
            </Link>
            <Link
              to="/contact"
              className="text-[#555] hover:text-white text-sm transition-colors cursor-pointer flex items-center gap-2"
            >
              Contact
              <i className="ri-arrow-right-line text-xs" />
            </Link>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}
